import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Card, List, Badge, Spin, Tabs, Button, message } from 'antd';
import { DocService } from '../../../services/dataService';

const SuggestionRow = ({ label, value }) => {
  // Hide row entirely if there is no value
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  let display = value;

  if (Array.isArray(display)) {
    display = display.join(', ');
  } else if (typeof display === 'boolean') {
    display = display ? 'Yes' : 'No';
  }

  return (
    <div className="flex gap-1 border-b border-neutral-100 py-2 w-[700px]">
      <div className="text-sm font-semibold w-1/2">{label}</div>
      <div className="text-sm w-1/2 flex flex-wrap gap-1">{display}</div>
    </div>
  );
};

const StatusDot = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  let color = 'bg-neutral-300';
  if (s === 'resolved') color = 'bg-green-500';
  else if (s === 'rejected') color = 'bg-red-500';

  return (
    <span className={`inline-block w-2 h-2 rounded-full ${color}`} title={s.charAt(0).toUpperCase() + s.slice(1)} />
  );
};

const SuggestionApprovalView = ({ articleId }) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(['approveArticleDetail', articleId], () => DocService.get(articleId), {
    enabled: !!articleId,
  });

  const article = data;
  const suggestions = article?.suggestions || [];

  const approveMutation = useMutation(
    (suggestion) =>
      DocService.updateSuggestions(articleId, {
        ...suggestion,
        status: 'resolved',
        resolved_at: new Date().toISOString(),
      }),
    {
      onSuccess: () => {
        message.success('Suggestion approved');
        queryClient.invalidateQueries(['approveArticleDetail', articleId]);
        queryClient.invalidateQueries(['suggestionArticles']);
      },
      onError: () => {
        message.error('Failed to approve suggestion');
      },
    },
  );

  const rejectMutation = useMutation(
    (suggestion) =>
      DocService.updateSuggestions(articleId, {
        ...suggestion,
        status: 'rejected',
        resolved_at: new Date().toISOString(),
      }),
    {
      onSuccess: () => {
        message.success('Suggestion rejected');
        queryClient.invalidateQueries(['approveArticleDetail', articleId]);
        queryClient.invalidateQueries(['suggestionArticles']);
      },
      onError: () => {
        message.error('Failed to reject suggestion');
      },
    },
  );

  if (!articleId) {
    return null;
  }

  if (isLoading || !article) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Article header */}
      <div className="mb-4">
        <div className="text-sm font-semibold text-neutral-800 mb-1">{article.title}</div>
        {article.authors && article.authors.length > 0 && (
          <div className="text-xs text-neutral-500 mb-1">{article.authors.join(', ')}</div>
        )}
        <div className="text-xs text-neutral-500 flex gap-3">
          {article.pubmed_id && <span>PMID: {article.pubmed_id}</span>}
          {article.published_year && <span>{article.published_year}</span>}
          {article.drug && <span>{article.drug}</span>}
          {article.task_specific_details?.primary_tissue_type && (
            <span>{article.task_specific_details.primary_tissue_type}</span>
          )}
        </div>
      </div>

      {suggestions.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-xs text-neutral-400">
          No suggestions for this article.
        </div>
      ) : (
        <Tabs
          defaultActiveKey={suggestions[0].id || '0'}
          className="flex-1 flex flex-col"
          items={suggestions.map((sugg, idx) => ({
            key: sugg.id || String(idx),
            label: (
              <span className="flex items-center gap-2">
                <StatusDot status={sugg.status} />
                <span>Suggestion {idx + 1}</span>
              </span>
            ),
            children: (
              <div className="flex flex-col gap-6 text-base text-neutral-600">
                {/* Article Details */}
                <div className="mb-6 text-neutral-500">
                  <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">
                    Article Details
                  </div>
                  <SuggestionRow label="Study Type" value={sugg.study_type} />
                  <SuggestionRow label="Is a Ad-Hoc/Post-Hoc Study" value={sugg.is_adhoc} />
                  <SuggestionRow label="Number of Patients" value={sugg.num_patients} />
                  <SuggestionRow label="Publication Country" value={sugg.publication_country} />
                </div>

                {/* Cancer Specific Details */}
                {sugg.nccn_cancer_types || sugg.primary_tissue_type ? (
                  <div className="mb-6">
                    <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">
                      Cancer Specific Details
                    </div>
                    <SuggestionRow label="NCCN Cancer Types" value={sugg.nccn_cancer_types} />
                    <SuggestionRow label="Primary Tissue Type" value={sugg.primary_tissue_type} />
                  </div>
                ) : null}

                {/* Drug Details */}
                {sugg.is_drug_evaluated || sugg.is_combination_therapy ? (
                  <div className="mb-6">
                    <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">Drug Details</div>
                    <SuggestionRow label="Is Queried Drug Evaluated" value={sugg.is_drug_evaluated} />
                    <SuggestionRow label="Combination" value={sugg.is_combination_therapy} />
                  </div>
                ) : null}

                {/* Outcomes */}
                {sugg.impact_type ? (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-blue-500 flex items-center gap-2 mb-2">Outcomes</div>
                    <SuggestionRow label="Therapeutic Association" value={sugg.impact_type} />
                  </div>
                ) : null}

                <div className="flex items-center justify-between text-xs text-neutral-500 mt-2">
                  <div className="flex flex-col">
                    <span className="font-medium text-neutral-600">Suggested by {sugg.suggested_by}</span>
                    {sugg.suggested_at && (
                      <span className="text-[11px]">{new Date(sugg.suggested_at).toLocaleString()}</span>
                    )}
                  </div>
                  {(() => {
                    const status = (sugg.status || 'pending').toLowerCase();
                    const label = status.charAt(0).toUpperCase() + status.slice(1);
                    let classes = 'inline-flex items-center px-2 py-0.5 rounded-full border text-[11px]';
                    if (status === 'resolved') {
                      classes += ' bg-green-50 border-green-200 text-green-600';
                    } else if (status === 'rejected') {
                      classes += ' bg-red-50 border-red-200 text-red-600';
                    } else {
                      classes += ' bg-neutral-50 border-neutral-200 text-neutral-600';
                    }
                    return <span className={classes}>{label}</span>;
                  })()}
                </div>

                <div className="flex gap-2 mt-3">
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => approveMutation.mutate(sugg)}
                    loading={approveMutation.isLoading}
                    disabled={sugg.status === 'resolved'}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    size="small"
                    onClick={() => rejectMutation.mutate(sugg)}
                    loading={rejectMutation.isLoading}
                    disabled={sugg.status === 'rejected'}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ),
          }))}
        />
      )}
    </div>
  );
};

const ApproveChanges = () => {
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading } = useQuery(['suggestionArticles'], () => DocService.listSuggestionArticles());

  const articles = data?.results || [];

  // Select the first article by default when data loads
  React.useEffect(() => {
    if (!selectedId && articles.length > 0) {
      setSelectedId(articles[0].id);
    }
  }, [articles, selectedId]);

  return (
    <div className="h-full flex gap-4">
      <div className="w-[360px] border-r border-neutral-200 pr-2 flex flex-col">
        <div className="mb-2 text-sm font-semibold text-neutral-600">Articles with Suggested Changes</div>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Spin />
          </div>
        ) : (
          <List
            dataSource={articles}
            renderItem={(item) => (
              <List.Item key={item.id} onClick={() => setSelectedId(item.id)}>
                <Card
                  className={`cursor-pointer px-2 py-4 ${
                    selectedId === item.id ? 'bg-blue-50 border-l-2 border-blue-500' : 'hover:bg-neutral-50'
                  }`}
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="flex flex-col w-full">
                    {/* Top pills row */}
                    <div className="flex items-center gap-2 mb-2">
                      {/* <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          item.is_relevant
                            ? 'text-green-600 bg-green-50 border-green-200'
                            : 'text-red-500 bg-red-50 border-red-200'
                        }`}
                      >
                        {item.is_relevant ? 'Include' : 'Exclude'}
                      </span> */}
                      {item.disease && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-600">
                          {item.disease}
                        </span>
                      )}
                      {item.drug && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-600">
                          {item.drug}
                        </span>
                      )}
                      {item.published_year && (
                        <span className="text-xs px-2 py-0.5 rounded-full border border-neutral-200 text-neutral-600">
                          {item.published_year}
                        </span>
                      )}
                      {item.unresolved_suggestion_count ? (
                        <Badge count={item.unresolved_suggestion_count} style={{ backgroundColor: '#2563eb' }} />
                      ) : null}
                    </div>

                    {/* Title */}
                    <div className="text-sm font-medium text-neutral-900 mb-1 line-clamp-2">
                      {item.title || 'Untitled article'}
                    </div>

                    {/* Authors */}
                    {item.authors && item.authors.length > 0 && (
                      <div className="text-xs text-neutral-500 mb-2 truncate">{item.authors.join(', ')}</div>
                    )}

                    {/* Bottom row buttons */}
                    <div className="flex items-center gap-2 mt-1">
                      {item.pubmed_id && (
                        <button
                          className="text-xs px-2 py-0.5 rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://pubmed.ncbi.nlm.nih.gov/${item.pubmed_id}`, '_blank');
                          }}
                        >
                          PubMed
                        </button>
                      )}
                      <button
                        className="text-xs px-2 py-0.5 rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(item.id);
                        }}
                      >
                        Open
                      </button>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
            style={{ height: '100%', overflowY: 'auto' }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        {selectedId ? (
          <SuggestionApprovalView articleId={selectedId} />
        ) : (
          <div className="h-full flex items-center justify-center text-neutral-400 text-sm">
            No articles with pending suggestions.
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveChanges;
